/**
 * Reporting period context — the FY + sub-period the reports are scoped to.
 * @file src/web/features/admin/lib/period-context.tsx
 *
 * Loads the imported voucher date range (per the selected entity), builds
 * the available Financial Years, and holds the selection (FY + Full year /
 * quarter / month / custom). Exposes the effective { from, to } ISO dates
 * that the report pages pass to the API. Persisted in localStorage.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { adminFetchJson } from './admin-api';
import { useCompany } from './company-context';

export interface FYOption { startYear: number; label: string }

interface Ctx {
  bounds: { min: string | null; max: string | null };
  fyOptions: FYOption[];
  startYear: number | null;
  sub: string; // 'full' | 'q1'..'q4' | 'm0'..'m11' | 'custom'
  from: string; // effective ISO from
  to: string; // effective ISO to
  setStartYear: (y: number) => void;
  setSub: (s: string) => void;
  setCustom: (from: string, to: string) => void;
  customFrom: string;
  customTo: string;
  ready: boolean;
}

const PeriodContext = createContext<Ctx | null>(null);
const KEY = 'oddits:period';

function fyOf(iso: string): number {
  const d = new Date(iso);
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; // Apr(3)–Mar
}

/** Effective { from, to } for an FY-start-year + sub-period. */
export function rangeFor(startYear: number, sub: string, customFrom?: string, customTo?: string): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const full = { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` };
  if (sub === 'custom') return { from: customFrom || full.from, to: customTo || full.to };
  if (sub === 'full' || !sub) return full;
  const q: Record<string, { from: string; to: string }> = {
    q1: { from: `${startYear}-04-01`, to: `${startYear}-06-30` },
    q2: { from: `${startYear}-07-01`, to: `${startYear}-09-30` },
    q3: { from: `${startYear}-10-01`, to: `${startYear}-12-31` },
    q4: { from: `${startYear + 1}-01-01`, to: `${startYear + 1}-03-31` },
  };
  if (q[sub]) return q[sub];
  if (sub.startsWith('m')) {
    const idx = Number(sub.slice(1)); // 0 = Apr … 11 = Mar
    const monthNum = idx < 9 ? idx + 4 : idx - 8;
    const year = idx < 9 ? startYear : startYear + 1;
    const last = new Date(year, monthNum, 0).getDate();
    return { from: `${year}-${pad(monthNum)}-01`, to: `${year}-${pad(monthNum)}-${pad(last)}` };
  }
  return full;
}

export const MONTH_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

export function PeriodProvider({ children }: { children: ReactNode }) {
  const { entityId } = useCompany();
  const [bounds, setBounds] = useState<{ min: string | null; max: string | null }>({ min: null, max: null });
  const [ready, setReady] = useState(false);
  const saved = (() => { try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; } })();
  const [startYear, setYear] = useState<number | null>(saved.startYear ?? null);
  const [sub, setSubState] = useState<string>(saved.sub || 'full');
  const [customFrom, setCF] = useState<string>(saved.customFrom || '');
  const [customTo, setCT] = useState<string>(saved.customTo || '');

  useEffect(() => {
    let cancel = false;
    setReady(false);
    adminFetchJson<{ min: string | null; max: string | null }>('/api/tally-import/periods', { query: { entityId: entityId ?? undefined } })
      .then((b) => {
        if (cancel) return;
        setBounds(b);
        setYear((cur) => {
          if (!b.max) return null;
          const latest = fyOf(b.max);
          const earliest = b.min ? fyOf(b.min) : latest;
          return cur != null && cur >= earliest && cur <= latest ? cur : latest;
        });
      })
      .catch(() => { if (!cancel) setBounds({ min: null, max: null }); })
      .finally(() => !cancel && setReady(true));
    return () => { cancel = true; };
  }, [entityId]);

  const persist = (patch: any) => localStorage.setItem(KEY, JSON.stringify({ startYear, sub, customFrom, customTo, ...patch }));
  const setStartYear = (y: number) => { setYear(y); persist({ startYear: y }); };
  const setSub = (s: string) => { setSubState(s); persist({ sub: s }); };
  const setCustom = (f: string, t: string) => { setCF(f); setCT(t); setSubState('custom'); persist({ customFrom: f, customTo: t, sub: 'custom' }); };

  const fyOptions = useMemo<FYOption[]>(() => {
    if (!bounds.max) return [];
    const latest = fyOf(bounds.max);
    const earliest = bounds.min ? fyOf(bounds.min) : latest;
    const out: FYOption[] = [];
    for (let y = latest; y >= earliest; y--) out.push({ startYear: y, label: `${y}-${String((y + 1) % 100).padStart(2, '0')}` });
    return out;
  }, [bounds]);

  const { from, to } = startYear != null ? rangeFor(startYear, sub, customFrom, customTo) : { from: '', to: '' };

  return (
    <PeriodContext.Provider value={{ bounds, fyOptions, startYear, sub, from, to, setStartYear, setSub, setCustom, customFrom, customTo, ready }}>
      {children}
    </PeriodContext.Provider>
  );
}

export const usePeriod = () => {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error('usePeriod must be used within PeriodProvider');
  return ctx;
};
